import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, User } from '../../../types';

interface ProjectConfigActionsProps {
  project: Project;
  currentUser: User | null;
  memberCount?: number;
  onEditProject: () => void;
  onStyleConfig: () => void;
  // mobile：把三项设置收进扳手下拉，腾出顶栏横向空间
  compact?: boolean;
  // 仅保留「项目设置/风格设置」两项，隐藏「协作者」——用于 Step4 强制态回看前两步设置。
  settingsOnly?: boolean;
}

/**
 * 顶栏「项目设置 / 风格设置 / 协作者」三个按钮（仅按钮，无弹窗）。
 *
 * 注意：配置弹窗（EditProjectDialog/StyleConfigDialog）留在各页面里渲染，由页面持有
 * useProjectConfigState(cfg) + open 状态——因为顶栏走 useHeaderSlot 按 deps 记忆化，
 * 若把频繁变化的 cfg 经 slot 传进来会导致弹窗输入不刷新。这里只把"开弹窗"的回调上抛。
 */
export default function ProjectConfigActions({ project, currentUser, memberCount = 0, onEditProject, onStyleConfig, compact = false, settingsOnly = false }: ProjectConfigActionsProps) {
  const { t } = useTranslation('projectDetail');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isOwner = project.ownerId === currentUser?.id && !settingsOnly;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (compact) {
    return (
      <div className="dropdown" ref={ref} style={{ position: 'relative' }}>
        <button
          className="btn btn-secondary btn-sm"
          aria-haspopup="true"
          aria-expanded={open}
          title={t('project.editProject')}
          onClick={() => setOpen(v => !v)}
          style={{ padding: '5px 9px', justifyContent: 'center' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
        </button>
        {open && (
          <div className="dropdown-menu" role="menu" style={{ right: 0, left: 'auto' }}>
            <button className="dropdown-item" role="menuitem" onClick={() => { setOpen(false); onEditProject(); }}>{t('project.editProject')}</button>
            <button className="dropdown-item" role="menuitem" onClick={() => { setOpen(false); onStyleConfig(); }}>{t('project.styleConfig')}</button>
            {isOwner && (
              <Link to={`/projects/${project.id}/members`} className="dropdown-item" role="menuitem" style={{ textDecoration: 'none' }} onClick={() => setOpen(false)}>{t('project.collaborators', { count: memberCount })}</Link>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
      <button className="btn btn-secondary btn-sm" style={{ justifyContent: 'center', gap: '0.4rem' }} onClick={onEditProject} title={t('project.editProject')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        <span className="hnav-label">{t('project.editProject')}</span>
      </button>
      <button className="btn btn-secondary btn-sm" style={{ justifyContent: 'center', gap: '0.4rem' }} onClick={onStyleConfig} title={t('project.styleConfig')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
        <span className="hnav-label">{t('project.styleConfig')}</span>
      </button>
      {isOwner && (
        <Link to={`/projects/${project.id}/members`} className="btn btn-secondary btn-sm" style={{ justifyContent: 'center', gap: '0.4rem', textDecoration: 'none' }} title={t('project.collaborators', { count: memberCount })}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          <span className="hnav-label">{t('project.collaborators', { count: memberCount })}</span>
        </Link>
      )}
    </div>
  );
}
